import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, LockSimple } from '@phosphor-icons/react';
import type { MappedApiError } from '@mybusinessmate/api-client';
import type { TaskDetail, Version } from '@mybusinessmate/schema';
import { checkDeliverableFile, DELIVERABLE_UPLOAD_TYPES, uploadDeliverableFile } from '../../lib/asset-upload';
import { initialSubmitValues, toSubmitRequest, type SubmitErrors, type SubmitValues } from '../../lib/submit-form';
import { useSaveVersion } from '../../lib/tasks-api';
import { useAssetUploads } from '../../lib/use-asset-uploads';
import { Icon } from '../Icon';
import { PageHeader } from '../shell/page-header';
import { Banner } from '../ui/banner';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { TextArea } from '../ui/text-area';
import { textLinkClass } from '../ui/text-link';
import { useToast } from '../ui/toast';
import { pillClass } from '../ui/toolbar-controls';
import { AssetUploader } from './asset-uploader';
import { ProgressTrack } from './progress-track';
import {
  CardHeading,
  PostFields,
  ReelFields,
  UpdateFields,
  WebsiteDetailsFields,
  WebsiteSectionsFields,
  type FieldsProps,
} from './submit-fields';

/** The words under the title for each type's file card (Figma A05.x submit). */
const FILES: Record<TaskDetail['taskType'], { title: string; description: string; hint: string; dropHint: string }> = {
  post: {
    title: 'Slides',
    description: 'The pictures that get posted, in order.',
    hint: 'Name each file for its slide: the lead sees the names.',
    dropHint: 'Drag in images · up to 500 MB',
  },
  reel: {
    title: 'Video file',
    description: 'The finished reel. Upload the file, or paste a link below if it is big.',
    hint: 'MP4 or MOV, vertical.',
    dropHint: 'Drag in the video · up to 500 MB',
  },
  website: {
    title: 'Source files (zip)',
    description: 'What the lead needs to review or deploy this build.',
    hint: 'Replaces the previous version’s zip.',
    dropHint: 'Drag in the zip · up to 500 MB',
  },
  monthly_update: {
    title: 'Screenshots',
    description: 'Before and after, so the lead can check the change.',
    hint: 'Images or a PDF.',
    dropHint: 'Drag in images · up to 500 MB',
  },
  custom: {
    title: 'Files',
    description: 'Everything you are handing over.',
    hint: 'Images, video, PDF or zip.',
    dropHint: 'Drag in files · up to 500 MB',
  },
};

const CARD_TITLE: Record<TaskDetail['taskType'], { title: string; description: string }> = {
  post: { title: 'Final post', description: 'What gets posted: the slides, the words and when it goes out.' },
  reel: { title: 'Final reel', description: 'What gets posted. Upload the file, or paste a link if it’s big.' },
  website: {
    title: 'Site details',
    description: 'Where the build lives and how to reach it. Logins go to the encrypted vault, not here.',
  },
  monthly_update: { title: 'What you changed', description: 'The page, and what was done to it.' },
  custom: { title: 'What you are handing over', description: 'Files and a note for the lead.' },
};

const firstName = (name: string | undefined) => name?.split(' ')[0] ?? 'the lead';

/** The name of the stop the slider has reached ("Built on staging"), for the line beside the percentage. */
function reached(task: TaskDetail, progress: number) {
  const stops = task.milestones.filter((m) => m.targetProgress <= progress);
  return stops.length > 0 ? stops[stops.length - 1]!.title : null;
}

/**
 * The page a specialist (or lead) hands a version back on (Figma A05.x submit): what they made, a note for the lead,
 * and where the slider stands. Sending it puts it in the lead's QA queue; it stays hidden from the client until a
 * lead, the account manager or an admin approves it. A draft can be saved and finished later.
 */
export function SubmitVersionPage({ task, versions }: { task: TaskDetail; versions: Version[] }) {
  const navigate = useNavigate();
  const toast = useToast();
  const save = useSaveVersion(task.id);
  const businessId = task.businessId ?? '';
  const start = initialSubmitValues(task, versions);
  const draft = start.draft;
  const carried = (draft ?? versions[0])?.assets ?? [];
  const uploads = useAssetUploads(businessId, carried, uploadDeliverableFile(task.id), checkDeliverableFile);
  const [values, setValues] = useState<SubmitValues>(start.values);
  const [errors, setErrors] = useState<SubmitErrors>({});
  const [formError, setFormError] = useState<string>();

  const number = draft?.versionNumber ?? (versions[0]?.versionNumber ?? 0) + 1;
  const lead = task.team.find((m) => m.staffRole === 'lead_specialist')?.name;
  const reviewer = firstName(lead);
  const live = versions.find((v) => v.isPublished);
  const waiting = versions.find((v) => v.internalStatus === 'pending_lead_review');
  const change = (patch: Partial<SubmitValues>) => setValues((v) => ({ ...v, ...patch }));

  const submit = async (asDraft: boolean) => {
    setFormError(undefined);
    if (uploads.busy) return setFormError('Wait for the files to finish uploading.');
    if (uploads.failed) return setFormError('A file didn’t upload. Retry it or remove it first.');

    const result = toSubmitRequest(values, task.taskType, uploads.assets, asDraft);
    if (!result.ok) {
      setErrors(result.errors);
      setFormError(result.errors.assets ?? 'Check the highlighted fields.');
      return;
    }
    setErrors({});
    try {
      await save.mutateAsync({ input: result.body, subId: draft?.id });
      toast({
        title: asDraft ? `Saved v${number} as a draft` : `v${number} is with ${reviewer} for QA`,
        description: asDraft ? undefined : 'The client doesn’t see it until it is approved.',
        tone: 'success',
      });
      await navigate({ to: '/subdomain/admin/tasks/$taskId', params: { taskId: task.id } });
    } catch (err) {
      const e = err as Partial<MappedApiError>;
      if (e.kind === 'validation' && e.fieldErrors?.length) {
        setErrors(Object.fromEntries(e.fieldErrors.map((f) => [f.field, f.message])));
        setFormError('Check the highlighted fields.');
      } else if (e.kind === 'forbidden') {
        setFormError('You can’t submit a version on this task.');
      } else if (e.status === 409) {
        setFormError(e.message ?? 'This task can’t take a new version right now.');
      } else {
        setFormError('We couldn’t save the version. Please try again.');
      }
    }
  };

  const fieldProps: FieldsProps = { values, onChange: change, errors, businessId, taskId: task.id };
  const files = FILES[task.taskType];
  const card = CARD_TITLE[task.taskType];
  const stop = reached(task, values.progress);
  const uploader = (
    <AssetUploader
      uploads={uploads}
      hint={files.hint}
      title={files.title}
      accept={DELIVERABLE_UPLOAD_TYPES}
      dropHint={files.dropHint}
      inputLabel="Add version files"
      layout={task.taskType === 'website' || task.taskType === 'reel' ? 'rows' : 'tiles'}
    />
  );

  return (
    <>
      <PageHeader
        title={`Submit version ${number}`}
        description={`${task.title}${task.businessName ? ` · ${task.businessName}` : ''} goes to ${lead ?? 'the lead'} for QA, hidden from the client until approved`}
        actions={
          <Link to="/subdomain/admin/tasks/$taskId" params={{ taskId: task.id }} className={pillClass('light', 'lg')}>
            <Icon icon={ArrowLeft} size="sm" />
            Back to task
          </Link>
        }
      />

      {waiting ? (
        <Banner tone="info" title={`v${waiting.versionNumber} is waiting on QA`}>
          One version is open at a time. {reviewer} will answer it first.
        </Banner>
      ) : null}
      {formError ? <Banner tone="error">{formError}</Banner> : null}

      <div className="grid items-start gap-(--mbm-space-6) lg:grid-cols-[minmax(0,1fr)_minmax(0,24rem)]">
        <div className="flex flex-col gap-(--mbm-space-5)">
          <Card tone="panel" padding="lg" className="flex flex-col gap-(--mbm-space-5)">
            <CardHeading title={card.title} description={card.description} />
            {task.taskType === 'website' ? <WebsiteDetailsFields {...fieldProps} zip={uploader} /> : null}
            {task.taskType === 'reel' ? (
              <ReelFields
                {...fieldProps}
                uploader={uploader}
                video={uploads.assets.find((asset) => asset.contentType.startsWith('video/'))}
              />
            ) : null}
            {task.taskType === 'post' || task.taskType === 'monthly_update' || task.taskType === 'custom'
              ? uploader
              : null}
            {errors.assets ? <p className="text-body-small text-feedback-error">{errors.assets}</p> : null}
            {task.taskType === 'post' ? <PostFields {...fieldProps} /> : null}
            {task.taskType === 'monthly_update' ? <UpdateFields {...fieldProps} /> : null}
          </Card>

          {task.taskType === 'website' ? (
            <Card tone="panel" padding="lg" className="flex flex-col gap-(--mbm-space-4)">
              <CardHeading
                title="Homepage sections"
                description="From the brief. Tick what’s built in this version; add screenshots so the lead and client can check."
                aside={
                  <span className="shrink-0 text-label-medium text-text-primary">
                    {values.sections.filter((s) => s.built).length} of {values.sections.length} built
                  </span>
                }
              />
              <WebsiteSectionsFields {...fieldProps} />
            </Card>
          ) : null}
        </div>

        <Card tone="panel" padding="lg" className="flex flex-col gap-(--mbm-space-4)">
          <h2 className="text-heading-h3 text-text-primary">Notes for {lead ?? 'the lead'}</h2>
          <TextArea
            aria-label={`Notes for ${lead ?? 'the lead'}`}
            variant="filled"
            density="compact"
            rows={5}
            value={values.notes}
            placeholder="What changed since the last version, and anything to look at first."
            onChange={(e) => change({ notes: e.target.value })}
          />

          <div className="flex flex-col gap-(--mbm-space-2)">
            <div className="flex items-baseline justify-between gap-(--mbm-space-3)">
              <span className="text-label-medium text-text-primary">Progress</span>
              <span className="text-label-medium text-text-primary">
                {values.progress}%{stop ? ` · ${stop}` : ''}
              </span>
            </div>
            <ProgressTrack
              value={values.progress}
              milestones={task.milestones}
              interactive
              labels={false}
              onCommit={(progress) => change({ progress })}
            />
          </div>

          <p className="flex items-start gap-(--mbm-space-2) rounded-(--mbm-radius-sm) bg-surface-tint p-(--mbm-space-3) text-body-small text-text-secondary">
            <span className="mt-(--mbm-space-0-5) shrink-0">
              <Icon icon={LockSimple} size="sm" />
            </span>
            <span>
              The client won’t see v{number} until {reviewer} approves it.
              {live ? ` They keep seeing v${live.versionNumber}.` : ''}
            </span>
          </p>

          <Button
            fullWidth
            disabled={save.isPending || !!waiting}
            loading={save.isPending}
            onClick={() => void submit(false)}
          >
            Submit v{number} for QA
          </Button>
          <button
            type="button"
            disabled={save.isPending || !!waiting}
            onClick={() => void submit(true)}
            className={`${textLinkClass} self-start`}
          >
            Save as draft instead
          </button>
        </Card>
      </div>
    </>
  );
}
